if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            this.modalContent.querySelector('product-component')?.dispatchViewEvent?.();

            super.show(opener);
            requestAnimationFrame(() => {
              this.prepareGallerySliders();
              this.querySelector('[id^="Slider-Gallery"]')?.scrollTo({ left: 0 });
            });
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.enableDesktopGallerySlider(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      enableDesktopGallerySlider(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.classList.add('slider--desktop', 'slider--tablet');

        const galleryControls = galleryList.parentElement?.querySelector(':scope > .slider-buttons');
        if (!galleryControls) return;

        galleryControls.classList.remove('quick-add-hidden');
        galleryControls.classList.add('quick-add-gallery-controls');
      }

      prepareGallerySliders() {
        this.querySelectorAll('slider-component').forEach((slider) => slider.resetPages?.());

        const mediaGallery = this.querySelector('media-gallery');
        if (!mediaGallery || mediaGallery.dataset.quickAddGalleryReady === 'true') return;

        mediaGallery.dataset.quickAddGalleryReady = 'true';
        const viewer = mediaGallery.querySelector('[id^="GalleryViewer"]');
        const thumbnailList = mediaGallery.querySelector('[id^="Slider-Thumbnails"]');
        if (!viewer || !thumbnailList) return;

        this.prepareGalleryControls(viewer);
        this.prepareQuickAddVideos(viewer);

        viewer.addEventListener('slideChanged', (event) => {
          const mediaId = event.detail.currentElement?.dataset.mediaId;
          if (!mediaId) return;

          const activeThumbnail = thumbnailList.querySelector(`[data-target="${mediaId}"]`);
          if (!activeThumbnail) return;

          thumbnailList.querySelectorAll('button').forEach((button) => button.removeAttribute('aria-current'));
          activeThumbnail.querySelector('button')?.setAttribute('aria-current', true);
          activeThumbnail.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
          this.playActiveQuickAddVideo(viewer, event.detail.currentElement);
        });
      }

      prepareQuickAddVideos(viewer) {
        const videos = viewer.querySelectorAll('video');
        if (!videos.length) return;

        videos.forEach((video) => {
          video.muted = true;
          video.defaultMuted = true;
          video.setAttribute('muted', '');
          video.setAttribute('playsinline', '');
          video.setAttribute('webkit-playsinline', '');
          video.setAttribute('autoplay', '');
          video.autoplay = true;
        });

        requestAnimationFrame(() => {
          const activeSlide =
            viewer.sliderItemsToShow?.find((item) => item.clientWidth > 0 && viewer.isSlideVisible(item, 1)) ||
            viewer.sliderItemsToShow?.[0];

          this.playActiveQuickAddVideo(viewer, activeSlide);
        });
      }

      playActiveQuickAddVideo(viewer, activeSlide) {
        const videos = viewer.querySelectorAll('video');
        if (!videos.length) return;

        videos.forEach((video) => {
          if (activeSlide?.contains(video)) {
            video.muted = true;
            video.defaultMuted = true;
            video.setAttribute('muted', '');
            video.setAttribute('playsinline', '');
            video.setAttribute('webkit-playsinline', '');
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      }

      prepareGalleryControls(viewer) {
        const controls = viewer.querySelector('.quick-add-gallery-controls');
        const previousButton = controls?.querySelector('button[name="previous"]');
        const nextButton = controls?.querySelector('button[name="next"]');
        if (!controls || !previousButton || !nextButton || !viewer.slider) return;

        const updateControls = () => {
          const items = Array.from(viewer.sliderItemsToShow || []).filter((item) => item.clientWidth > 0);
          const firstItem = items[0];
          const lastItem = items[items.length - 1];
          if (!firstItem || !lastItem) return;

          previousButton.toggleAttribute('disabled', viewer.slider.scrollLeft <= 1 || viewer.isSlideVisible(firstItem));
          nextButton.toggleAttribute('disabled', viewer.isSlideVisible(lastItem, 1));
        };

        controls.addEventListener(
          'click',
          (event) => {
            const button = event.target.closest('button');
            if (!button) return;

            updateControls();
            if (button.hasAttribute('disabled')) {
              event.preventDefault();
              event.stopImmediatePropagation();
              if (button.name === 'previous') viewer.slider.scrollTo({ left: 0 });
            }
          },
          true
        );

        viewer.slider.addEventListener('scroll', updateControls);
        updateControls();
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
